import React, { useRef, useEffect, useState } from "react";
import { Html5Qrcode, Html5QrcodeCameraScanConfig } from "html5-qrcode";
import { checkInAttendee } from "../../services/eventService";
import { useToast } from "../../contexts/ToastContext";

interface BookingDetails {
  event_id?: number;
  event_title?: string;
  registration_token: string;
  user_full_name?: string;
  number_of_attendees?: number;
  status?: string;
  status_label?: string;
}

interface QRCodeScannerProps {
  onScanSuccess: (registrationToken: string) => Promise<BookingDetails>;
  onCheckInSuccess?: (attendeesCount: number) => void;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScanSuccess,
  onCheckInSuccess,
}) => {
  const [scanResult, setScanResult] = useState<BookingDetails | null>(null);
  const [invalidQr, setInvalidQr] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("user");
  const [scanning, setScanning] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const { showSuccessToast, showErrorToast } = useToast();

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const scannerStartedRef = useRef(false);
  const qrRegionId = "html5qr-code-full-region";

  useEffect(() => {
    if (!scanResult && !invalidQr) {
      // Cleanup previous instance if exists
      if (html5QrCodeRef.current && scannerStartedRef.current) {
        html5QrCodeRef.current
          .stop()
          .then(() => html5QrCodeRef.current?.clear())
          .catch(() => {}); // Ignore errors on cleanup
        scannerStartedRef.current = false;
      }

      const html5QrCode = new Html5Qrcode(qrRegionId);

      const config: Html5QrcodeCameraScanConfig = {
        fps: 30,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      html5QrCode
        .start(
          { facingMode: facingMode === "user" ? "user" : "environment" },
          config,
          async (decodedText: string) => {
            if (scanning) return;
            setScanning(true);

            try {
              setInvalidQr(false);
              setError(null);
              const data = JSON.parse(decodedText || "{}");
              if (data.booking_token) {
                const details = await onScanSuccess(data.booking_token);
                setTimeout(() => setScanResult(details), 500);
                if (scannerStartedRef.current) {
                  await html5QrCode.stop();
                  await html5QrCode.clear();
                  scannerStartedRef.current = false;
                }
              } else {
                setError("Invalid QR code format.");
              }
            } catch (e) {
              if (
                e &&
                typeof e === "object" &&
                "response" in e &&
                e.response &&
                typeof e.response === "object" &&
                "data" in e.response &&
                e.response.data &&
                typeof e.response.data === "object" &&
                "message" in e.response.data
              ) {
                // @ts-ignore
                console.log(e.response.data.message);
                setError(
                  String(e.response.data.message) ||
                    "Failed to fetch booking details."
                );
                setInvalidQr(true);
              } else {
                setInvalidQr(true);
                setError("Failed to parse QR code.");
              }
            } finally {
              setScanning(false);
            }
          },
          (err) => {
            // Optionally handle error
          }
        )
        .then(() => {
          html5QrCodeRef.current = html5QrCode;
          scannerStartedRef.current = true;
        })
        .catch((err) => {
          setError("Unable to start the camera. " + String(err));
        });

      // Cleanup on unmount
      return () => {
        if (html5QrCodeRef.current && scannerStartedRef.current) {
          html5QrCodeRef.current
            .stop()
            .then(() => html5QrCodeRef.current?.clear())
            .catch(() => {});
          scannerStartedRef.current = false;
        }
      };
    }
    // eslint-disable-next-line
  }, [facingMode, scanResult, invalidQr]);

  const onCheckInClicked = async () => {
    try {
      setCheckInLoading(true);
      const response = await checkInAttendee(
        scanResult?.event_id || 0,
        scanResult?.registration_token || ""
      );
      showSuccessToast(response.data.message);
      if (scanResult?.number_of_attendees) {
        onCheckInSuccess?.(scanResult.number_of_attendees);
      }
      setScanResult(null);
      setError(null);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Event not found.");
    } finally {
      setCheckInLoading(false);
    }
  };

  return (
    <>
      {!scanResult && !invalidQr ? (
        <>
          <div className="flex flex-col align-items-center justify-content-center p-4">
            <div className="rounded-xl overflow-hidden">
              <div id={qrRegionId} style={{ width: 320, height: 320 }} />
            </div>
          </div>
          <div className="flex flex-col align-items-center justify-content-center p-4">
            <button
              className="thm-btn small_btn"
              onClick={() =>
                setFacingMode((prev) =>
                  prev === "environment" ? "user" : "environment"
                )
              }
            >
              Switch to {facingMode === "environment" ? "Front" : "Back"} Camera
            </button>
          </div>
        </>
      ) : scanResult ? (
        <div className="flex flex-col align-items-center justify-content-center p-4">
          <div className="bg-gray-100 p-4 rounded-xl shadow-md w-full max-w-sm text-center">
            {scanResult.status == "checked-in" ? (
              <p className="text-green-700 font-bold mb-2">
                ✅ Already Checked In
              </p>
            ) : (
              <p className="text-red-700 font-bold mb-2">
                ✅ Booking Confirmed
              </p>
            )}
            <p>
              <b>Event:</b> {scanResult.event_title}
            </p>
            <p>
              <b>Token:</b> {scanResult.registration_token}
            </p>
            <p>
              <b>Name:</b> {scanResult.user_full_name}
            </p>
            <p>
              <b>Number of Attendees:</b> {scanResult.number_of_attendees}
            </p>
            <p>
              <b>Status:</b> {scanResult.status_label}
              {scanResult.status == "checked-in" && <span>✅</span>}
              {(scanResult.status == "cancelled" ||
                scanResult.status == "refunded") && <span>❌</span>}
            </p>
            {scanResult.status == "registered" && (
              <button
                className="thm-btn small_btn mt-4"
                onClick={onCheckInClicked}
                disabled={checkInLoading}
              >
                Check-in
              </button>
            )}
            <button
              className="thm-black-btn small_btn mt-4"
              onClick={() => {
                setScanResult(null);
                setError(null);
              }}
            >
              Scan Another QR
            </button>
          </div>
        </div>
      ) : invalidQr ? (
        <div className="flex flex-col align-items-center justify-content-center p-4">
          <div className="bg-gray-100 p-4 rounded-xl shadow-md w-full max-w-sm text-center">
            <p className="text-red-700 font-bold mb-2">❌ Invalid QR Code</p>
            {error && <p className="mb-2">{error}</p>}
            <button
              className="thm-black-btn small_btn mt-4"
              onClick={() => {
                setInvalidQr(false);
                setError(null);
              }}
            >
              Scan Again
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default QRCodeScanner;
